import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, FileText, ShieldCheck, Globe } from "lucide-react";
import Logo3D from "./Logo3D";
import { useTheme } from "@/lib/theme";
import { useI18n } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { Button } from "@/components/ui/button";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useI18n();
  const { content } = useContent();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navItems = [
    { id: "benefits", label: t("nav.benefits") },
    { id: "process", label: t("nav.process") },
    { id: "countries", label: t("nav.countries") },
    { id: "vacancies", label: t("nav.vacancies") },
    { id: "team", label: t("nav.team") },
    { id: "reviews", label: t("nav.reviews"), path: "/reviews" },
    { id: "payment", label: t("nav.payment"), path: "/payment", requiresVisible: "payment" },
    { id: "contacts", label: t("nav.contacts") },
  ].filter((it) => !it.requiresVisible || content?.visibility?.[it.requiresVisible] !== false);

  const licenseUrl = content?.license?.file_url || "/initial/docs/license.pdf";

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-border/60" : ""
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center" data-testid="logo-home-link">
          <Logo3D size={38} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 p-1 rounded-full border border-border/60 bg-background/40 backdrop-blur">
          {navItems.map((it) => (
            it.path ? (
              <Link
                key={it.id}
                to={it.path}
                className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                data-testid={`nav-${it.id}`}
                onClick={() => setOpen(false)}
              >
                {it.label}
              </Link>
            ) : (
              <button
                key={it.id}
                onClick={() => goSection(it.id)}
                className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                data-testid={`nav-${it.id}`}
              >
                {it.label}
              </button>
            )
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "ru" ? "en" : "ru")}
            className="hidden sm:flex items-center gap-1.5 px-4 h-10 rounded-full border border-border/70 text-xs font-semibold uppercase tracking-wider hover:bg-muted/60 transition-colors"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Globe className="size-3.5" />
            {lang}
          </button>

          <button
            onClick={toggle}
            className="size-10 rounded-full border border-border/70 flex items-center justify-center hover:bg-muted/60 transition-colors"
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            <AnimatePresence mode="wait">
              {theme === "dark" ? (
                <motion.span key="sun" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <Sun className="size-4" />
                </motion.span>
              ) : (
                <motion.span key="moon" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <Moon className="size-4" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <a
            href={licenseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
            data-testid="license-btn"
          >
            <ShieldCheck className="size-4" />
            {t("nav.license")}
          </a>

          <button
            className="lg:hidden size-10 rounded-full border border-border/70 flex items-center justify-center"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="burger-btn"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden glass border-t border-border"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {navItems.map((it) => (
                it.path ? (
                  <Link
                    key={it.id}
                    to={it.path}
                    onClick={() => setOpen(false)}
                    className="text-left px-3 py-3 text-sm font-medium hover:bg-muted rounded-full transition-colors"
                    data-testid={`mobile-nav-${it.id}`}
                  >
                    {it.label}
                  </Link>
                ) : (
                  <button
                    key={it.id}
                    onClick={() => goSection(it.id)}
                    className="text-left px-3 py-3 text-sm font-medium hover:bg-muted rounded-full transition-colors"
                    data-testid={`mobile-nav-${it.id}`}
                  >
                    {it.label}
                  </button>
                )
              ))}
              <a
                href={licenseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 px-4 py-3 rounded-md bg-foreground text-background text-sm font-semibold"
                data-testid="mobile-license-btn"
              >
                <ShieldCheck className="size-4" /> {t("nav.license")}
              </a>
              <button
                onClick={() => setLang(lang === "ru" ? "en" : "ru")}
                className="mt-1 inline-flex items-center gap-2 px-4 py-3 rounded-md border border-border text-sm font-medium"
                data-testid="mobile-lang-toggle"
              >
                <Globe className="size-4" /> {lang.toUpperCase()}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
